import { CONNECTOR_LIMITS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon, Input, Skeleton } from "@sagot/ui";
import { useId, useState } from "react";
import { usePermissions } from "../../../../session/permissions";
import type { ConnectorCallStep } from "../types";
import { callPlaceholders, callSample, callTestProblem, testParamsOf } from "./connector-op-model";
import { operationTestFailureOf, useOperationTest, type OperationTestOut } from "./use-connector-ops";

const C = "engage.workflows.connector.op.test";

interface TestProps {
  step: ConnectorCallStep;
  /** The plan does not include the connector step: nothing can be tested. */
  locked: boolean;
  planName: string;
  disabled: boolean;
}

function SampleFields({ names, values, disabled, onChange }: { names: string[]; values: Record<string, string>; disabled: boolean; onChange: (name: string, value: string) => void }) {
  const { t } = useTranslation();
  if (names.length === 0) return null;
  return (
    <fieldset className="space-y-2" data-testid="op-sample">
      <legend className="label">{t(`${C}.sample`)}</legend>
      <p className="hint -mt-1">{t(`${C}.sampleHint`)}</p>
      {names.map((name) => (
        <label key={name} className="flex flex-wrap items-center gap-2 text-xs">
          <code className="w-full font-mono font-semibold sm:w-48 sm:shrink-0">{`{{${name}}}`}</code>
          <Input className="min-w-0 flex-1 font-mono text-xs" placeholder={t(`${C}.samplePlaceholder`)} disabled={disabled} maxLength={CONNECTOR_LIMITS.sampleValue} value={values[name] ?? ""} onChange={(event) => { onChange(name, event.target.value); }} />
        </label>
      ))}
    </fieldset>
  );
}

function Result({ result }: { result: OperationTestOut }) {
  const { t, i18n } = useTranslation();
  const values = Object.entries(result.mapping?.values ?? {});
  const status = result.response?.status ?? 0;
  const message = result.error ? (i18n.language === "fil" ? result.error.message_fil : result.error.message) : null;
  return (
    <div className="space-y-3" data-testid="op-result">
      <div className="space-y-1" data-testid="op-verdict">
        <p className={result.ok ? "flex items-center gap-2 text-sm font-semibold text-success" : "flex items-center gap-2 text-sm font-semibold text-danger"}>
          <Icon name={result.ok ? "check-circle" : "alert-triangle"} className="size-4 shrink-0" />
          {t(`${C}.${result.ok ? "succeeded" : "failed"}`)}
        </p>
        <p className="flex flex-wrap items-center gap-2 text-xs text-fg-muted">
          <Badge tone={result.ok ? "success" : "danger"}>{status > 0 ? t(`${C}.status`, { status }) : t(`${C}.noStatus`)}</Badge>
          <span className="font-mono">{t(`${C}.duration`, { ms: result.duration_ms })}</span>
          <span>{t(`${C}.environment`, { environment: result.environment })}</span>
        </p>
        {message ? <p className="text-xs text-fg-muted" data-testid="op-error">{message}</p> : null}
        {result.refuse ? <p className="text-xs text-fg-muted" data-testid="op-refused">{t(`${C}.refused.${result.refuse}`, { defaultValue: t(`${C}.refused.unknown`) })}</p> : null}
      </div>
      {result.ok ? (
        <div>
          <h4 className="label">{t(`${C}.valuesTitle`)}</h4>
          {values.length === 0 ? (
            <p className="text-xs text-fg-muted">{t(`${C}.valuesNone`)}</p>
          ) : (
            <dl className="space-y-1 text-xs" data-testid="op-values">
              {values.map(([name, value]) => (
                <div key={name} className="flex flex-wrap gap-x-2">
                  <dt className="font-mono font-semibold">{name}</dt>
                  <dd className="break-all font-mono text-fg-muted">{String(value)}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Test: sends the operation once through the Connectors test console with sample parameters; the answer comes back masked. */
export function OperationTest({ step, locked, planName, disabled }: TestProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const permissions = usePermissions();
  const test = useOperationTest(step.connector_id, step.operation_key);
  const [typed, setTyped] = useState<Record<string, string>>({});
  const names = callPlaceholders(step);
  const incomplete = callTestProblem(step);
  const allowed = permissions.isPending || permissions.can("connectors.test");
  const blocked = locked || incomplete !== null || !allowed;
  const reason = locked ? t(`${C}.locked`, { plan: planName }) : incomplete !== null ? t(`${C}.needOperation`) : !allowed ? t(`${C}.noAccess`) : null;
  const run = () => {
    if (!test.isPending) test.mutate({ params: testParamsOf(step, callSample(step, typed)), dry_run: false });
  };
  return (
    <section aria-labelledby={headingId} className="space-y-3 rounded-md bg-surface-2 p-3" data-testid="op-test">
      <div>
        <h3 id={headingId} className="text-sm font-bold">{t(`${C}.title`)}</h3>
        <p className="mt-0.5 text-xs text-fg-muted">{t(`${C}.intro`)}</p>
      </div>
      <SampleFields names={names} values={callSample(step, typed)} disabled={disabled || test.isPending} onChange={(name, value) => { setTyped({ ...typed, [name]: value }); }} />
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="secondary" size="sm" loading={test.isPending} disabled={disabled || blocked} aria-describedby={reason ? `${headingId}-reason` : undefined} data-testid="op-test-run" onClick={run}>
          <Icon name="play" />
          {test.isPending ? t(`${C}.running`) : t(`${C}.run`)}
        </Button>
        {reason ? <span id={`${headingId}-reason`} className="text-xs text-fg-muted">{reason}</span> : null}
      </div>
      <div aria-live="polite" aria-busy={test.isPending}>
        {test.isPending ? (
          <div role="status" aria-label={t(`${C}.running`)} className="space-y-2" data-testid="op-test-loading">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-64 max-w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : null}
        {test.isError ? (
          <p role="alert" className="flex items-start gap-2 text-sm font-semibold text-danger" data-testid="op-test-error">
            <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0" />
            {t(`${C}.callFailed.${operationTestFailureOf(test.error)}`)}
          </p>
        ) : null}
        {test.isSuccess ? <Result result={test.data} /> : null}
      </div>
    </section>
  );
}
